import { createSlice } from '@reduxjs/toolkit'
import {
  takeHoldemSeatThunk,
  takeHoldemActionThunk,
  updateHoldemPlayerSittingStatusThunk,
  updateHoldemBuyInAmountThunk,
  changeHoldemSeatThunk,
  getHoldemGameTablesThunk
} from 'redux-thunk/thunk/holdemGameManagement.thunk'

const initialState = {
  gameData: null,
  gameState: 'idle',
  holdemGameTables: [],
  currentSeat: null,
  playerActions: [],
  gamePot: 0,
  communityCards: [],
  currentPlayer: null,
  loading: false,
  // Specific loading states for different actions
  takingSeatLoading: false,
  takingActionLoading: false,
  error: null,
  pixi: {
    isAssetsLoaded: false,
    isInitialized: false
  }
}

const holdemSlice = createSlice({
  name: 'holdem',
  initialState,
  reducers: {
    setGameData: (state, action) => {
      state.gameData = action.payload
    },
    setGameState: (state, action) => {
      state.gameState = action.payload
    },
    setCurrentSeat: (state, action) => {
      state.currentSeat = action.payload
    },
    addPlayerAction: (state, action) => {
      state.playerActions.push(action.payload)
    },
    setGamePot: (state, action) => {
      state.gamePot = action.payload
    },
    setCommunityCards: (state, action) => {
      state.communityCards = action.payload
    },
    setCurrentPlayer: (state, action) => {
      state.currentPlayer = action.payload
    },
    clearError: (state) => {
      state.error = null
    },
    setPixiAssetsLoaded: (state, action) => {
      state.pixi.isAssetsLoaded = action.payload
    },
    setPixiInitialized: (state, action) => {
      state.pixi.isInitialized = action.payload
    },
    updateTableSeatOccupied: (state, action) => {
      const { tableId, seatsOccupied } = action.payload
      const tableIndex = state.holdemGameTables.findIndex(table => String(table.id) === String(tableId))
      if (tableIndex !== -1) {
        state.holdemGameTables[tableIndex].seatsOccupied = seatsOccupied
      }
    }
  },
  extraReducers: (builder) => {
    // Take Holdem Seat
    builder
      .addCase(takeHoldemSeatThunk.pending, (state) => {
        state.takingSeatLoading = true
        state.error = null
      })
      .addCase(takeHoldemSeatThunk.fulfilled, (state, action) => {
        state.takingSeatLoading = false
        state.currentSeat = action.payload
      })
      .addCase(takeHoldemSeatThunk.rejected, (state, action) => {
        state.takingSeatLoading = false
        state.error = action.error.message
      })

    // Take Holdem Action
    builder
      .addCase(takeHoldemActionThunk.pending, (state) => {
        state.takingActionLoading = true
        state.error = null
      })
      .addCase(takeHoldemActionThunk.fulfilled, (state, action) => {
        state.takingActionLoading = false
        state.playerActions.push(action.payload)
        // Update pot if action affects it
        if (action.payload.potAmount) {
          state.gamePot = action.payload.potAmount
        }
      })
      .addCase(takeHoldemActionThunk.rejected, (state, action) => {
        state.takingActionLoading = false
        state.error = action.error.message
      })

    // Update Holdem Player Sitting Status
    builder
      .addCase(updateHoldemPlayerSittingStatusThunk.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateHoldemPlayerSittingStatusThunk.fulfilled, (state, action) => {
        state.loading = false
        // Update game data with new sitting status
        if (state.gameData) {
          state.gameData.playerSittingStatus = action.payload
        }
      })
      .addCase(updateHoldemPlayerSittingStatusThunk.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message
      })

    // Update Holdem Buy In Amount
    builder
      .addCase(updateHoldemBuyInAmountThunk.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateHoldemBuyInAmountThunk.fulfilled, (state, action) => {
        state.loading = false
        // Update game data with new buy-in amount
        if (state.gameData) {
          state.gameData.buyInAmount = action.payload
        }
      })
      .addCase(updateHoldemBuyInAmountThunk.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message
      })

    // Change Holdem Seat
    builder
      .addCase(changeHoldemSeatThunk.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(changeHoldemSeatThunk.fulfilled, (state, action) => {
        state.loading = false
        state.currentSeat = action.payload
      })
      .addCase(changeHoldemSeatThunk.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message
      })

    // Get Holdem Game Tables
    builder
      .addCase(getHoldemGameTablesThunk.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getHoldemGameTablesThunk.fulfilled, (state, action) => {
        state.loading = false
        const data = action.payload?.data ?? action.payload
        // Backend returns { tables: [...], pagination: {...} }
        const tables = Array.isArray(data) ? data : (data?.tables ?? data?.rows ?? [])
        state.holdemGameTables = tables.map(table => ({
          ...table,
          seatsOccupied: table.seatsOccupied !== undefined
            ? table.seatsOccupied
            : table?.tableSeats?.filter(seat => seat.userId !== null && seat.sittingOut === false).length || 0
        }))
      })
      .addCase(getHoldemGameTablesThunk.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message
      })
  }
})

export const {
  setGameData,
  setGameState,
  setCurrentSeat,
  addPlayerAction,
  setGamePot,
  setCommunityCards,
  setCurrentPlayer,
  clearError,
  updateTableSeatOccupied,

  // pixi
  setPixiAssetsLoaded,
  setPixiInitialized
} = holdemSlice.actions

export default holdemSlice.reducer
